import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { getMasterFileRecords } from "../lib/api";

const NAICS_SECTORS = {
  '11': 'Agriculture, Forestry & Fishing',
  '21': 'Mining, Quarrying & Oil/Gas',
  '22': 'Utilities',
  '23': 'Construction',
  '31': 'Manufacturing', '32': 'Manufacturing', '33': 'Manufacturing',
  '42': 'Wholesale Trade',
  '44': 'Retail Trade',  '45': 'Retail Trade',
  '48': 'Transportation & Warehousing', '49': 'Transportation & Warehousing',
  '51': 'Information',
  '52': 'Finance & Insurance',
  '53': 'Real Estate & Rental',
  '54': 'Professional & Technical Services',
  '55': 'Management of Companies',
  '56': 'Administrative & Support Services',
  '61': 'Educational Services',
  '62': 'Health Care & Social Assistance',
  '71': 'Arts, Entertainment & Recreation',
  '72': 'Accommodation & Food Services',
  '81': 'Other Services',
  '92': 'Public Administration',
};

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

function getFilterTitle(filter, sector, country, state) {
  if (state && country)  return `${state}, ${country}`;
  if (country)           return `Country: ${country}`;
  const MAP = {
    all:          "All Records",
    names:        "Company Names Cleaned",
    address:      "Addresses Standardized",
    domain:       "Websites Found",
    naics:        "NAICS Codes Assigned",
    naics_sector: `${NAICS_SECTORS[sector.split(',')[0].trim()] || sector || ""} Sector`,
    unclassified: "Not Classified (No NAICS)",
    profile:      "Business Profiles",
    person:       "People Identified",
    internal:     "Intercompany Records",
    error:        "Errors",
  };
  return MAP[filter] || "Records";
}

function getRelevantHeader(filter) {
  if (filter === "naics" || filter === "naics_sector" || filter === "unclassified") return "NAICS";
  if (filter === "address") return "City / State";
  if (filter === "profile") return "About";
  if (filter === "person" || filter === "internal") return "Type";
  if (filter === "error")   return "Error";
  return "Domain";
}

// Map filter → sort column key for the relevant column
function getRelevantSortCol(filter) {
  if (filter === "domain" || filter === "all" || filter === "names") return "domain_value";
  if (filter === "naics" || filter === "naics_sector") return "naics_code";
  if (filter === "address") return "city_std";
  return null; // not sortable for profile/person/internal/error/unclassified
}

function confidenceBadge(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return null;
  const pct   = n > 1 ? n : Math.round(n * 100);
  const color = pct >= 90 ? "#10b981" : pct >= 70 ? "#f59e0b" : "#ef4444";
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
      background: `${color}18`, color, marginLeft: 6 }}>
      {pct}%
    </span>
  );
}

function truncate(s, n) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n) + "…" : s;
}

function SortTh({ label, col, sortCol, sortDir, onSort, style }) {
  const active = sortCol === col;
  return (
    <th
      style={{ ...styles.th, cursor: col ? "pointer" : "default", userSelect: "none", whiteSpace: "nowrap", ...style }}
      onClick={() => col && onSort(col)}
    >
      {label}
      {col && (
        <span style={{ marginLeft: 4, color: active ? "#0176CE" : "#cbd5e1", fontSize: 9 }}>
          {active ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
        </span>
      )}
    </th>
  );
}

function RelevantCell({ filter, row }) {
  if (filter === "domain" || filter === "all" || filter === "names") {
    return (
      <td style={styles.td}>
        {row.domain_value
          ? <>{row.domain_value}{confidenceBadge(row.domain_confidence)}</>
          : <span style={{ color: "#d1d5db" }}>—</span>}
      </td>
    );
  }
  if (filter === "naics" || filter === "naics_sector") {
    return (
      <td style={styles.td}>
        {row.naics_code
          ? <>
              <span style={{ fontFamily: "monospace", fontSize: 12, color: "#6c63ff", marginRight: 6 }}>{row.naics_code}</span>
              <span style={{ color: "#6b7280", fontSize: 12 }}>{truncate(row.naics_title, 30)}</span>
            </>
          : <span style={{ color: "#d1d5db" }}>—</span>}
      </td>
    );
  }
  if (filter === "unclassified") {
    return <td style={styles.td}><span style={{ color: "#d1d5db", fontStyle: "italic" }}>Not classified</span></td>;
  }
  if (filter === "address") {
    const loc = [row.city_std, row.state_std].filter(Boolean).join(", ");
    return <td style={styles.td}><span style={{ fontSize: 13, color: "#374151" }}>{loc || "—"}</span></td>;
  }
  if (filter === "profile") {
    return <td style={styles.td}><span style={{ fontSize: 12, color: "#6b7280" }}>{truncate(row.what_they_do, 60) || "—"}</span></td>;
  }
  if (filter === "person")   return <td style={styles.td}><span style={styles.badgeAmber}>Person</span></td>;
  if (filter === "internal") return <td style={styles.td}><span style={styles.badgePurple}>Internal</span></td>;
  if (filter === "error")    return <td style={styles.td}><span style={{ fontSize: 12, color: "#ef4444" }}>{truncate(row.error_message, 50) || "Error"}</span></td>;
  const loc = [row.city_std, row.state_std, row.country_std].filter(Boolean).join(", ");
  return <td style={styles.td}><span style={{ fontSize: 12, color: "#374151" }}>{loc || "—"}</span></td>;
}

function StatusBadge({ ok }) {
  if (ok) return <span style={{ fontSize: 11, fontWeight: 600, color: "#10b981", background: "rgba(16,185,129,0.1)", borderRadius: 5, padding: "2px 8px" }}>✓ OK</span>;
  return     <span style={{ fontSize: 11, fontWeight: 600, color: "#ef4444", background: "rgba(239,68,68,0.08)", borderRadius: 5, padding: "2px 8px" }}>✗ Error</span>;
}

function TableRow({ row, filter, sector }) {
  const [hover, setHover] = useState(false);
  const navigate = useNavigate();
  const detailFilter = (filter === "naics_sector") ? `naics_sector&sector=${sector}` : filter;
  const linkFilter   = (filter === "country" || filter === "state") ? "all" : detailFilter;

  return (
    <tr
      style={{ ...styles.tr, background: hover ? "#fafbff" : "#fff", cursor: "pointer" }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => navigate(`/results/${row.last_job_id}/records/${row.row_number}?filter=${linkFilter}`)}
    >
      <td style={{ ...styles.td, color: "#94a3b8", fontSize: 11 }}>{row.system_name}</td>
      <td style={{ ...styles.td, color: "#6b7280", fontSize: 12 }}>{row.original_num || "—"}</td>
      <td style={styles.td}><span style={{ color: "#9ca3af", fontSize: 12 }}>{row.original_name || "—"}</span></td>
      <td style={styles.td}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ color: "#111827", fontWeight: 500, fontSize: 13 }}>
            {row.company_name_recommended || row.name1_std || "—"}
          </span>
          {row.total_addresses > 1 && (
            <span style={{ fontSize: 10, fontWeight: 600, color: "#6c63ff", background: "rgba(108,99,255,0.08)", borderRadius: 4, padding: "1px 5px", flexShrink: 0 }}>
              {row.total_addresses} addr
            </span>
          )}
        </div>
      </td>
      <RelevantCell filter={filter} row={row} />
      <td style={styles.td}><StatusBadge ok={row.ok} /></td>
    </tr>
  );
}

export default function MasterFileRecords() {
  const navigate          = useNavigate();
  const [searchParams]    = useSearchParams();

  const filter  = searchParams.get("filter")  || "all";
  const sector  = searchParams.get("sector")  || "";
  const country = searchParams.get("country") || "";
  const state   = searchParams.get("state")   || "";
  const missing = searchParams.get("missing") === "1";

  const [records,     setRecords]     = useState([]);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(1);
  const [limit,       setLimit]       = useState(50);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search,      setSearch]      = useState("");
  const [sortCol,     setSortCol]     = useState("company_name_recommended");
  const [sortDir,     setSortDir]     = useState("asc");
  const debounceRef = useRef(null);

  function handleSearchChange(e) {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setSearch(val.trim()); setPage(1); }, 350);
  }

  function clearSearch() { setSearchInput(""); setSearch(""); setPage(1); }

  function handleSort(col) {
    if (sortCol === col) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
    setPage(1);
  }

  function handleLimitChange(e) {
    setLimit(Number(e.target.value));
    setPage(1);
  }

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const fetchPage = useCallback(async (p) => {
    setLoading(true);
    setError("");
    try {
      const res = await getMasterFileRecords({
        filter, sector, country, state, search, missing,
        page: p, limit, sort_col: sortCol, sort_dir: sortDir,
      });
      setTotal(res.total || 0);
      setRecords(res.records || []);
    } catch (e) {
      setError(e.message || "Could not load records.");
    } finally {
      setLoading(false);
    }
  }, [filter, sector, country, state, search, missing, limit, sortCol, sortDir]);

  useEffect(() => {
    fetchPage(page);
  }, [fetchPage, page]);

  // Reset to page 1 when filters/sort/limit change (fetchPage changes → useEffect fires with page=1)
  useEffect(() => {
    setPage(1);
  }, [filter, sector, country, state, search, missing, limit, sortCol, sortDir]);

  const pageTitle   = getFilterTitle(filter, sector, country, state);
  const relevantHdr = getRelevantHeader(filter);
  const relevantCol = getRelevantSortCol(filter);

  return (
    <div style={styles.page}>
      <div style={styles.topStripe} />
      <div style={styles.body}>
      <Sidebar active="master-file" />
      <main style={styles.main}>

        <div style={styles.headerRow}>
          <div>
            <Link to="/master-file" style={styles.backLink}>← Back to Master File</Link>
            <h1 style={styles.pageTitle}>{pageTitle}</h1>
            <p style={styles.pageSub}>{total.toLocaleString()} records</p>
          </div>
          <div style={{ position: "relative", width: 280 }}>
            <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: 14, pointerEvents: "none" }}>⌕</span>
            <input
              type="text"
              placeholder="Search vendor name, #, system…"
              value={searchInput}
              onChange={handleSearchChange}
              style={styles.searchInput}
            />
            {searchInput && (
              <button onClick={clearSearch} style={styles.searchClear}>✕</button>
            )}
          </div>
        </div>

        {loading && (
          <div style={styles.centerBox}>
            <div style={styles.spinner} />
            <p style={styles.mutedText}>Loading records…</p>
          </div>
        )}

        {!loading && error && (
          <div style={styles.errorCard}>
            <p style={{ color: "#f87171", margin: 0 }}>{error}</p>
            <button onClick={() => fetchPage(page)} style={styles.retryBtn}>Retry</button>
          </div>
        )}

        {!loading && !error && (
          <div style={styles.tableCard}>
            {records.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>⧗</div>
                <p style={{ color: "#6b7280", margin: 0, fontSize: 15 }}>No records found for this filter.</p>
              </div>
            ) : (
              <>
                {/* Table toolbar */}
                <div style={styles.tableHeaderBar}>
                  <span style={styles.tableCount}>
                    {((page - 1) * limit + 1).toLocaleString()}–{Math.min(page * limit, total).toLocaleString()} of {total.toLocaleString()} records
                  </span>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <label style={{ color: "#6b7280", fontSize: 12 }}>Show</label>
                    <select
                      value={limit}
                      onChange={handleLimitChange}
                      style={styles.limitSelect}
                    >
                      {PAGE_SIZE_OPTIONS.map(n => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <table style={styles.table}>
                  <thead>
                    <tr>
                      <SortTh label="System"        col="system_name"              sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                      <SortTh label="Vendor #"       col="record_number"            sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                      <SortTh label="Original Name"  col="record_name"              sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                      <SortTh label="Cleaned Name"   col="company_name_recommended" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                      <SortTh label={relevantHdr}    col={relevantCol}              sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                      <SortTh label="Status"         col={null}                     sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                    </tr>
                  </thead>
                  <tbody>
                    {records.map(row => (
                      <TableRow
                        key={row.master_record_id}
                        row={row}
                        filter={filter}
                        sector={sector}
                      />
                    ))}
                  </tbody>
                </table>

                {/* Pagination footer */}
                {totalPages > 1 && (
                  <div style={styles.pagination}>
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      style={{ ...styles.pageBtn, opacity: page === 1 ? 0.4 : 1 }}
                    >
                      ← Prev
                    </button>
                    <span style={{ color: "#6b7280", fontSize: 13 }}>
                      Page {page} of {totalPages.toLocaleString()}
                    </span>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      style={{ ...styles.pageBtn, opacity: page === totalPages ? 0.4 : 1 }}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </main>
      </div>
    </div>
  );
}

const styles = {
  page:         { display: "flex", flexDirection: "column", minHeight: "100vh", background: "#f8f9fb" },
  topStripe:    { height: 4, background: "linear-gradient(90deg, #0176CE, #F37428)", flexShrink: 0 },
  body:         { display: "flex", flex: 1 },
  main:         { flex: 1, padding: "36px 40px", overflowY: "auto" },
  headerRow:    { display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 28 },
  searchInput:  { width: "100%", padding: "9px 32px 9px 32px", fontSize: 13, border: "1px solid #e2e6ef", borderRadius: 8, outline: "none", background: "#fff", color: "#111827", boxSizing: "border-box" },
  searchClear:  { position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 13, padding: 0, lineHeight: 1 },
  backLink:     { color: "#6b7280", fontSize: 13, textDecoration: "none", display: "block", marginBottom: 8 },
  pageTitle:    { color: "#111827", fontSize: 28, fontWeight: 700, margin: 0, letterSpacing: "-0.5px" },
  pageSub:      { color: "#6b7280", fontSize: 14, margin: "4px 0 0" },
  centerBox:    { textAlign: "center", padding: "80px 24px", display: "flex", flexDirection: "column", alignItems: "center" },
  mutedText:    { color: "#6b7280", marginTop: 16, fontSize: 14 },
  spinner:      { width: 32, height: 32, border: "3px solid #e9ecf3", borderTop: "3px solid #6c63ff", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  errorCard:    { background: "#fff", border: "1px solid #fecaca", borderRadius: 12, padding: "32px 24px", textAlign: "center" },
  retryBtn:     { marginTop: 12, background: "transparent", border: "1px solid #6c63ff", borderRadius: 6, color: "#6c63ff", cursor: "pointer", fontSize: 13, padding: "7px 16px" },
  tableCard:    { background: "#fff", border: "1px solid #e2e6ef", borderRadius: 12, overflow: "hidden" },
  tableHeaderBar:{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 24px", borderBottom: "1px solid #e2e6ef" },
  tableCount:   { color: "#6b7280", fontSize: 13 },
  limitSelect:  { border: "1px solid #e2e6ef", borderRadius: 6, fontSize: 13, color: "#374151", padding: "4px 8px", background: "#fff", cursor: "pointer" },
  table:        { width: "100%", borderCollapse: "collapse" },
  th:           { color: "#6b7280", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", padding: "11px 16px", textAlign: "left", background: "#f8f9fb", borderBottom: "1px solid #e2e6ef" },
  tr:           { borderBottom: "1px solid #e9ecf3", transition: "background 0.1s" },
  td:           { color: "#374151", fontSize: 13, padding: "12px 16px", verticalAlign: "middle" },
  pagination:   { display: "flex", alignItems: "center", justifyContent: "center", gap: 16, padding: "14px 24px", borderTop: "1px solid #e2e6ef" },
  pageBtn:      { background: "#fff", border: "1px solid #e2e6ef", borderRadius: 7, color: "#374151", cursor: "pointer", fontSize: 13, padding: "7px 16px" },
  emptyState:   { textAlign: "center", padding: "60px 24px", display: "flex", flexDirection: "column", alignItems: "center" },
  badgeAmber:   { fontSize: 11, fontWeight: 600, color: "#f59e0b", background: "rgba(245,158,11,0.1)", borderRadius: 5, padding: "2px 8px" },
  badgePurple:  { fontSize: 11, fontWeight: 600, color: "#8b5cf6", background: "rgba(139,92,246,0.1)", borderRadius: 5, padding: "2px 8px" },
};
